// @LTG-ID: data/journals/earnout-business-sale
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0108
// @CHANGED: 2026-07-06 (CHG-0108 · Journal port completion, articles 21-40)
// @OWNER: web
//
// Article 25 of 40. Source: batch MD at /app/_ltg_content_batches/batch_3_4/.
// Byline: LTG Team (per CHG-0107.6 §17.3 convention).
// Section: Deal Process (per CHG-0108 D4 orchestrator adjudication).

export const BRIEF = {
    SLUG: "earnout-business-sale",
    FRONTMATTER: {
        number: "62",
        section: "Deal Process",
        sectionHref: "/journals#deal-process",
        sectionAnchor: "deal-process",
        title: "Earnouts Explained: Getting Paid Based on the Business's Future Performance",
        deck: "An earnout is a portion of the sale price paid only if the business hits agreed performance targets, usually revenue or profit levels, over one to three years after closing.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-06-06",
        publishedHuman: "June 6, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 455,
        timeRequired: "PT3M",
        keywords: "earnout business sale, what is an earnout, earnout structure, earnout when selling a company",
        articleSection: "Deal Process",
        articleBodyPreview: "An earnout is a portion of the sale price paid only if the business hits agreed performance targets, usually revenue or profit levels, over one to three years after closing. Earnouts bridge price disagreements and manage buyer risk, but they are dispute prone and depend on decisions the seller no longer controls. An earnout is the deal world's answer to an argument. The seller says the business is about to grow; the buyer says prove it. The earnout says: fine, we will let the next two years deci",
        complianceNote: "This article is educational and does not constitute legal or financial advice.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "customer-concentration-selling-business",
        "seller-financing-business-sale"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Earnouts: Getting Paid on Future Business Performance · LTG",
        canonical: "https://letstalkgrowth.com/journals/earnout-business-sale",
        ogImage: "/journals/og/earnout-business-sale.jpg",
    },
    BODY_MARKDOWN: `An earnout is a portion of the sale price paid only if the business hits agreed performance targets, usually revenue or profit levels, over one to three years after closing. Earnouts bridge price disagreements and manage buyer risk, but they are dispute-prone and depend on decisions the seller no longer controls.

An earnout is the deal world's answer to an argument. The seller says the business is about to grow; the buyer says prove it. The earnout says: fine, we will let the next two years decide, with money on the outcome.

## How earnouts are built

A typical structure pays a fixed amount at closing plus additional payments contingent on performance during a measurement period, commonly one to three years. Four design choices define every earnout, and each is a negotiation:

- **The metric.** Revenue is hardest to manipulate and simplest to verify; profit-based metrics track what buyers actually care about but open arguments about every expense the new owner books. Gross profit sits usefully between the extremes.
- **The targets.** Thresholds, and whether payment is all-or-nothing at a cliff or scales proportionally. Sellers should be wary of cliffs; near-misses on all-or-nothing targets are where the bitterest disputes live.
- **The period.** Long enough to be meaningful, short enough that the seller is not hostage to years of someone else's management.
- **The operating rules.** The most overlooked and most important part: covenants about how the buyer will run the business during the period, because the seller's payout now depends on decisions the seller no longer makes.

## When earnouts appear, and why sellers accept them

Earnouts show up predictably: when a business's recent growth is not yet proven durable, when value is concentrated in relationships or customers the buyer fears losing, when customer concentration needs a retention bridge, and whenever buyer and seller are genuinely stuck on price. Accepting one can rescue a good deal, and sellers who hit their targets do get paid. But every experienced advisor gives the same discount advice: treat the closing payment as the price, and the earnout as upside. A deal you would not sign for its guaranteed portion alone is a deal you are hoping into, not negotiating.

## Protecting an earnout you have agreed to

The protections are contractual and must be drafted, not assumed: clear metric definitions with accounting methods frozen as of closing, access to the books during the measurement period, operating covenants preventing the buyer from starving the business or rerouting its revenue, acceleration if the buyer resells the company mid-period, and a defined dispute process. An attorney who negotiates earnouts regularly is not optional here; the difference between a collectible earnout and a lawsuit is almost entirely in the drafting.`,
    FAQ: [
        { question: "Are earnouts good or bad for sellers?", answer: "They are a risk transfer. Earnouts let deals close across a price disagreement, and sellers who hit targets collect full value. But payment depends on performance under the buyer's management, so sellers should evaluate the deal primarily on its guaranteed portion." },
        { question: "What is the most common earnout dispute?", answer: "Disagreement over the metric's calculation, especially profit-based metrics, where the buyer's post-closing expense and allocation decisions move the number. Freezing accounting definitions in the agreement and securing audit rights prevents most of it." },
        { question: "How long do earnout periods last?", answer: "Most run one to three years. Shorter periods reduce the seller's exposure to the buyer's management; longer periods suit genuinely long-maturing growth stories. The measurement period should match the specific uncertainty the earnout exists to resolve." }
    ],
    SOURCES: [
    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal \u2192",
        microLine: "\u00b7 7-DAY DELIVERY \u00b7 KGOB METHODOLOGY, NC CPA #30420 \u00b7",
    },
    READ_NEXT: [
        {
            tag: "Value Drivers",
            title: "Customer Concentration: How Much Is Too Much When Selling a Business?",
            meta: "3 min · Brief 50",
            to: "/journals/customer-concentration-selling-business",
        },
        {
            tag: "Deal Process",
            title: "Seller Financing: How It Works and When It Makes Sense",
            meta: "3 min · Brief 61",
            to: "/journals/seller-financing-business-sale",
        },
    ],
};

export default BRIEF;
